import React, { CSSProperties, useEffect } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { SxStyle } from '../../../types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RemoveIcon from '@mui/icons-material/Remove';
import Chip from '@mui/material/Chip';
import firebaseApp from '../../../firebase-app';
import { useCollection } from 'react-firebase-hooks/firestore';
import groupBy from 'lodash/groupBy';
import moment from 'moment';
import LinearProgress from '@mui/material/LinearProgress';
import get from 'lodash/get';
import { useStringsContext } from '../../../contexts/strings';
import { collection } from 'firebase/firestore';

const styles: SxStyle = {
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  tableHeader: (theme) => ({
    fontSize: theme.typography.pxToRem(15),
  }),
  tableContainer: {
    overflow: 'auto',
    minHeight: '70vh',
    maxHeight: '70vh',
  },
  loadingContainer: {
    overflow: 'auto',
    minHeight: '70vh',
    maxHeight: '70vh',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },
  progress: {
    flex: 1,
    maxWidth: '50%',
  },
  logo: {
    width: '30px',
    height: '30px',
    objectFit: 'contain',
    position: 'relative',
    border: 0,
    '&:after': {
      content: '"" attr(alt)',
      position: 'absolute',
      border: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'white',
    },
  },
};

const formatGroupedData = (data: string | any[]) => {
  const newData = {
    matchId: data[0].matchId,
    homeTeam: data[0].teams ? data[0].teams[0] : null,
    awayTeam: data[0].teams ? data[0].teams[1] : null,
    numberOfMessages: data.length,
    messages: data,
  };

  return newData;
};

function Row({ row, teams }: InferProps<typeof Row.propTypes>) {
  const { strings } = useStringsContext();
  const [open, setOpen] = React.useState(false);

  const homeTeam = teams.filter((team: { abbrv: any }) => team.abbrv === row.homeTeam)[0];
  const homeTeamName = get(homeTeam, 'name', row.awayTeam);
  const homeTeamLogo = get(homeTeam, 'logoImageUrl', null);

  const awayTeam = teams.filter((team: { abbrv: any }) => team.abbrv === row.awayTeam)[0];
  const awayTeamName = get(awayTeam, 'name', row.awayTeam);
  const awayTeamLogo = get(awayTeam, 'logoImageUrl', null);

  return (
    <React.Fragment key={row.matchId}>
      <TableRow key={`${row.matchId}-1`} sx={styles.root}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Chip label={row.matchId} />
        </TableCell>
        <TableCell align="right">{homeTeamName}</TableCell>
        <TableCell align="right">
          <img style={styles.logo as CSSProperties} src={homeTeamLogo} height="30px" alt="" />
        </TableCell>
        <TableCell align="center">
          <RemoveIcon />
        </TableCell>
        <TableCell align="left">
          <img style={styles.logo as CSSProperties} src={awayTeamLogo} height="30px" alt="" />
        </TableCell>
        <TableCell align="left">{awayTeamName}</TableCell>
        <TableCell>{row.numberOfMessages}</TableCell>
      </TableRow>
      <TableRow key={`${row.matchId}-2`}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box>
              <Typography variant="h6" gutterBottom component="div">
                {strings.messagesViewer.messages}
              </Typography>
              <Table size="small">
                <TableHead sx={styles.tableHeader}>
                  <TableRow>
                    <TableCell sx={styles.tableHeader}>{strings.messagesViewer.type}</TableCell>
                    <TableCell sx={styles.tableHeader}>{strings.messagesViewer.created}</TableCell>
                    <TableCell sx={styles.tableHeader}>{strings.messagesViewer.expired}</TableCell>
                    <TableCell sx={styles.tableHeader}>{strings.messagesViewer.message}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.messages.map(
                    (historyRow: {
                      category: any;
                      created: any;
                      expires: any;
                      templateData: any;
                    }) => (
                      <TableRow key={historyRow && historyRow.created}>
                        <TableCell component="th" scope="row">
                          <Chip label={historyRow && historyRow.category} />
                        </TableCell>
                        <TableCell>
                          {historyRow && moment(historyRow.created).format('DD-MM-YYYY hh:mm:ss')}
                        </TableCell>
                        <TableCell>
                          {historyRow && moment(historyRow.expires).format('DD-MM-YYYY hh:mm:ss')}
                        </TableCell>
                        <TableCell>{historyRow && historyRow.templateData.text}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.any,
  teams: PropTypes.any,
};

export default function MessageTable({
  currentAppFilter,
  setMessageSummary,
}: InferProps<typeof MessageTable.propTypes>) {
  const { strings } = useStringsContext();
  const [messages, loading] = useCollection(
    collection(firebaseApp.db, `/messages/for-${currentAppFilter.toUpperCase()}/archivedMessage`)
  );
  const [currentMessages, currentMessagesLoading] = useCollection(
    collection(firebaseApp.db, `/messages/for-${currentAppFilter.toUpperCase()}/messages`)
  );

  const [teamsRef] = useCollection(collection(firebaseApp.db, `/teams`));
  const teams = teamsRef?.docs.map((doc: any) => doc.data());

  useEffect(() => {
    const tempMessages = messages?.docs.map((doc: any) => doc.data()) || [];
    const numberOfMatches = Object.keys(groupBy(tempMessages, 'matchId')).length;
    const totalMessagesSent = tempMessages.length || 0;
    setMessageSummary({
      numberOfMatches,
      totalMessagesSent: totalMessagesSent,
      averageMessagesPerMatch: numberOfMatches && Math.round(totalMessagesSent / numberOfMatches),
    });
    return () => {};
  }, [messages, setMessageSummary]);

  if (loading || currentMessagesLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <LinearProgress sx={styles.progress} />
      </Box>
    );
  }

  if (messages && !loading && currentMessages && !currentMessagesLoading) {
    const archivedMessagesArray = messages?.docs.map((doc: any) => doc.data());
    const archivedGroupedBy = groupBy(archivedMessagesArray, 'matchId');
    const archivedKeys = Object.keys(archivedGroupedBy);
    const archivedFormattedMessages = archivedKeys
      ? archivedKeys.map((key: any) => formatGroupedData(archivedGroupedBy[key]))
      : [];

    const messagesArray = currentMessages?.docs.map((doc: any) => doc.data());
    const groupedBy = groupBy(messagesArray, 'matchId');
    const keys = Object.keys(groupedBy);
    const formattedMessages = keys.map((key: any) => formatGroupedData(groupedBy[key]));
    const allMessages = archivedFormattedMessages.concat(formattedMessages).reverse();

    return (
      <TableContainer sx={styles.tableContainer} component={Paper}>
        <Table stickyHeader>
          <TableHead sx={styles.tableHeader}>
            <TableRow>
              <TableCell sx={styles.tableHeader} />
              <TableCell sx={styles.tableHeader}>Match ID</TableCell>
              <TableCell sx={styles.tableHeader} align="right">
                {strings.messagesViewer.homeTeam}
              </TableCell>
              <TableCell sx={styles.tableHeader} />
              <TableCell sx={styles.tableHeader} />
              <TableCell sx={styles.tableHeader} />
              <TableCell sx={styles.tableHeader}>{strings.messagesViewer.awayTeam}</TableCell>
              <TableCell sx={styles.tableHeader}>
                {strings.messagesViewer.numberOfMessages}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allMessages.map((row) => (
              <Row row={row} teams={teams} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return null;
}

MessageTable.propTypes = {
  currentAppFilter: PropTypes.string.isRequired,
  setMessageSummary: PropTypes.func.isRequired,
};
